/**
 * Responsibility: the auth context object and the hook that reads it.
 *
 * Split from `AuthProvider` because a file that exports both a component and a
 * hook breaks fast refresh - the same reason `gradeBuckets.ts` exists apart
 * from the components that use it.
 */
import { createContext, useContext } from 'react';

export type Identity = {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
};

export type AuthState = {
  /** Null when signed out. */
  user: Identity | null;
  /** True until Firebase has had a chance to restore a stored session. */
  loading: boolean;
  /** False when this build carries no Firebase config at all. */
  configured: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
};

export const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const found = useContext(AuthContext);
  if (found === null) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return found;
}
